import { useState, useMemo, useEffect } from "react";
import { usePagination } from "../../../../hooks/usePagination";
import { COLLECTIONS } from "../../../../services/collections";
import { collection, query, where, getDocs } from "firebase/firestore";
import { db } from "../../../../services/firebase";

const today = () => new Date().toISOString().split("T")[0];

const getThirtyDaysAgo = () => {
    const d = new Date();
    d.setDate(d.getDate() - 30);
    return d.toISOString().split("T")[0];
};

/**
 * Converte "YYYY-MM-DD" para Date no início do dia (00:00:00) no fuso local.
 */
const toStartOfDay = (isoStr) => {
    const [y, m, d] = isoStr.split("-").map(Number);
    return new Date(y, m - 1, d, 0, 0, 0, 0);
};

/**
 * Converte "YYYY-MM-DD" para Date no final do dia (23:59:59.999) no fuso local.
 */
const toEndOfDay = (isoStr) => {
    const [y, m, d] = isoStr.split("-").map(Number);
    return new Date(y, m - 1, d, 23, 59, 59, 999);
};

export const useCancellations = (idTenant) => {
    const [search, setSearch] = useState("");
    const [startDate, setStartDate] = useState(getThirtyDaysAgo());
    const [endDate, setEndDate] = useState(today());
    
    // Flag para controlar se o usuário já clicou em "Buscar"
    const [hasFetched, setHasFetched] = useState(false);

    // Estados internos aplicados (para evitar busca instantânea ao digitar na data)
    // Começam null — só recebem valor após o clique em "Buscar"
    const [appliedStartDate, setAppliedStartDate] = useState(null);
    const [appliedEndDate, setAppliedEndDate] = useState(null);

    const [memberStatuses, setMemberStatuses] = useState({});
    const [fetchingStatuses, setFetchingStatuses] = useState(false);

    // Filtros do Firestore: cancelamentos no período
    // Usa updatedAt (Timestamp real do Firestore) em vez de cancelDate (string inflada por jobs)
    const firestoreFilters = useMemo(() => {
        if (!appliedStartDate || !appliedEndDate) return null;
        return [
            { field: "status", operator: "==", value: "canceled" },
            { field: "updatedAt", operator: ">=", value: toStartOfDay(appliedStartDate) },
            { field: "updatedAt", operator: "<=", value: toEndOfDay(appliedEndDate) },
        ];
    }, [appliedStartDate, appliedEndDate]);

    const applyDateFilters = () => {
        setMemberStatuses({});
        setAppliedStartDate(startDate);
        setAppliedEndDate(endDate);
        setHasFetched(true);
    };

    // Paginação Genérica do Firestore
    // Só dispara quando firestoreFilters for um array válido (após o clique em Buscar)
    const {
        data: records,
        loading: paginationLoading,
        error,
        hasMore,
        loadMore,
        refresh,
    } = usePagination(
        `tenants/${idTenant}/${COLLECTIONS.MEMBER_MEMBERSHIPS}`,
        hasFetched ? idTenant : null, // Passar null impede a busca (usePagination verifica idTenant)
        {
            pageSize: 30,
            orderField: "updatedAt",
            orderDirection: "desc",
            filters: firestoreFilters || [],
            skip: !hasFetched,
        }
    );

    // Efeito para buscar o status atual do aluno e saber se ele é churn real
    useEffect(() => {
        const fetchStatuses = async () => {
            if (!records || records.length === 0) return;
            
            const uniqueIds = [...new Set(records.map(r => r.idMember).filter(Boolean))];
            const idsToFetch = uniqueIds.filter(id => memberStatuses[id] === undefined);
            
            if (idsToFetch.length === 0) return;

            setFetchingStatuses(true);
            try {
                // Firestore 'in' query suporta até 10 itens
                for (let i = 0; i < idsToFetch.length; i += 10) {
                    const chunk = idsToFetch.slice(i, i + 10);
                    const q = query(
                        collection(db, "tenants", idTenant, COLLECTIONS.MEMBERS),
                        where("__name__", "in", chunk)
                    );
                    const snap = await getDocs(q);
                    const newStatuses = {};
                    
                    // Inicializa todos com null para sabermos que já buscamos e não existe
                    chunk.forEach(id => newStatuses[id] = null);
                    
                    snap.forEach(doc => {
                        newStatuses[doc.id] = doc.data().status;
                    });
                    
                    setMemberStatuses(prev => ({ ...prev, ...newStatuses }));
                }
            } catch (err) {
                console.error("Erro ao buscar status dos membros:", err);
            } finally {
                setFetchingStatuses(false);
            }
        };

        fetchStatuses();
    }, [records, idTenant, memberStatuses]);

    // Filtrar primeiro por Churn real, depois por busca textual
    const filteredRecords = useMemo(() => {
        // 1. Filtrar Churn Real:
        let result = records.filter(r => {
            // Ignorar transferências
            if (r.isTransfer) return false;
            
            const mStatus = memberStatuses[r.idMember];
            
            // Se o status ainda não foi buscado, aguardar (retorna false para não piscar)
            if (mStatus === undefined) return false;
            
            // Se o aluno está ativo atualmente, não é churn (renovou ou tem outro plano)
            if (mStatus === "active") return false;
            
            return true;
        });

        // 2. Busca textual
        if (search.trim()) {
            const term = search.toLowerCase().trim();
            result = result.filter(r =>
                (r.membershipName || "").toLowerCase().includes(term) ||
                (r.memberName || "").toLowerCase().includes(term) ||
                (r.cancelReason || "").toLowerCase().includes(term)
            );
        }

        return result;
    }, [records, search, memberStatuses]);

    return {
        records: filteredRecords,
        loading: hasFetched && (paginationLoading || fetchingStatuses),
        error,
        hasMore,
        loadMore,
        refresh,
        search, setSearch,
        startDate, setStartDate,
        endDate, setEndDate,
        applyDateFilters,
        hasFetched,
    };
};
